import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Row, Table, Typography } from 'antd'
import Chart from 'react-apexcharts'
import { createUseStyles } from 'react-jss'
import ChartTableSwitch from './ChartTableSwitch'
import dayjs from '../../lib/datetime'

const { Title } = Typography

export default function ReportsStats({ reports }) {
  const [viewMode, setViewMode] = useState('chart')
  const classes = createUseStyles({
    inlineTitle: {
      display: 'inline-block',
    }
  })()

  const chartOptions = useMemo(() => {
    const colors = [  '#3695dc', '#5abf3f','#Fcaa58', '#Ff3d4a',]
    return {
      chart: {
        type: 'line',
        sparkline: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        stacked: false,
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'straight',
        width: [2, 2],
      },
      fill: {
        type: ['solid', 'solid'],
      },
      states: {
        normal: {
          filter: {
            type: 'none',
          },
        },
      },
      grid: {
        show: false,
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        clusterGroupedSeries: false,
      },
      markers: { size: [5, 5, 5, 5], shape: 'square' },
      xaxis: {
        categories: reports.map(report => dayjs(`${report.year}-${report.month}`).format("MMM YY")),
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        type: 'category',
      },
      yaxis: {
        labels: {
          formatter(value) {
            return `${Math.round(value)}`
          },
        },
        min: 0,
        max:
        Math.max(
          reports.reduce((max, report) => Math.max(max, report.remoteChecksCount || 0), 0),
          reports.reduce((max, report) => Math.max(max, report.alertsCount || 0), 0),
          reports.reduce((max, report) => Math.max(max, report.officeChecksCount || 0), 0),
        ),
        title: {
          text: '',
        },
      },
      colors,
      tooltip: {
        custom({ dataPointIndex }) {
          const report = reports[dataPointIndex]
          if (!report) return ''

          const monthText = `${dayjs(report.year + report.month).format("MMM YYYY")}`

          return `<div style="padding: 12px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <strong>${monthText}</strong>
            <p style="margin: 8px 0 0 0;">Remote Checks: ${report.remoteChecksCount || 0}</p>
            <p style="margin: 4px 0 0 0;">Alert Checks: ${report.officeChecksCount || 0}</p>
            <p style="margin: 4px 0 0 0;">Office Checks: ${report.alertsCount || 0}</p>
          </div>`
        },
      },
    }
  }, [reports])

  const chartSeries = [
    {
      name: 'Remote Checks',
      data: reports.map(report => report.remoteChecksCount || 0),
      type: 'line',
    },
    {
      name: 'Alert Checks',
      data: reports.map(report => report.alertsCount || 0),
      type: 'line',
    },
    {
      name: 'Office Checks',
      data: reports.map(report => report.officeChecksCount || 0),
      type: 'line',
    },
  ]

  const tableColumns = [
    {
      key: 'month',
      title: 'Month',
      render: (_, record) => dayjs(record.year + record.month).format('MMM YYYY'),
    },
    {
      key: 'remoteChecksCount',
      title: 'Remote Checks',
      dataIndex: 'remoteChecksCount',
      align: 'center',
    },
    {
      key: 'alertsCount',
      title: 'Alerts Checks',
      dataIndex: 'alertsCount',
      align: 'center',
    },
    {
      key: 'checksCount',
      title: 'Office Checks',
      dataIndex: 'officeChecksCount',
      align: 'center',
    },
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} className={classes.inlineTitle}>
            Device Checks
          </Title>
        </Col>
        <Col>
          <ChartTableSwitch onChange={setViewMode} viewMode={viewMode} />
        </Col>
      </Row>

      {viewMode === 'chart' && (
        <Row gutter={[0, 50]}>
          <Col span={24}>
            <Chart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height={350}
            />
          </Col>
        </Row>
      )}

      {viewMode === 'table' && (
        <Row gutter={[0, 50]}>
          <Col span={24}>
            <Table
              columns={tableColumns}
              dataSource={reports.map((report, idx) => ({ ...report, key: idx }))}
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </Col>
        </Row>
      )}
    </>
  )
}

ReportsStats.propTypes = {
  reports: PropTypes.arrayOf(
    PropTypes.shape({
      cycle: PropTypes.shape({
        id: PropTypes.number.isRequired,
        startsAt: PropTypes.string.isRequired,
        endsAt: PropTypes.string.isRequired,
      }).isRequired,
      reportsCount: PropTypes.number,
      checksCount: PropTypes.number,
      totalCount: PropTypes.number,
    }),
  ).isRequired,
}
